import { Bell, X } from 'lucide-react'
import { Popover } from '@base-ui/react/popover'
import { Button } from '../ui/button'
import { Modal } from '../ui/modal'
import { toast } from 'sonner'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  confirmAnnouncement,
  getAnnouncementSummary,
  listInboxAnnouncements,
  subscribeAnnouncementChanges,
  type AnnouncementItem,
  type AnnouncementSummary,
} from '../../services/announcements'

const DISMISS_KEY = 'kyren-announcement-important-dismissed'

interface AnnouncementShellProps {
  children: (
    announcementHeader: ReactNode,
    urgentAnnouncementActive: boolean,
    onAgreementDialogsClosed: () => void,
  ) => ReactNode
}

export default function AnnouncementShell({
  children,
}: AnnouncementShellProps) {
  const navigate = useNavigate()
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [summary, setSummary] = useState<AnnouncementSummary>({
    unreadCount: 0,
  })
  const [recent, setRecent] = useState<AnnouncementItem[]>([])
  const [confirming, setConfirming] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1',
  )
  const [summaryPending, setSummaryPending] = useState(true)
  const [agreementDialogsClosed, setAgreementDialogsClosed] = useState(false)
  const [urgentModalVisible, setUrgentModalVisible] = useState(false)
  const pendingRefreshes = useRef(0)
  const refreshSequence = useRef(0)
  const latestAppliedRefresh = useRef(0)
  const urgentAnnouncementActiveRef = useRef(true)

  const refresh = useCallback(async () => {
    const requestSequence = ++refreshSequence.current
    pendingRefreshes.current += 1
    if (!urgentAnnouncementActiveRef.current) setAgreementDialogsClosed(false)
    setSummaryPending(true)
    try {
      const response = await getAnnouncementSummary()
      if (requestSequence >= latestAppliedRefresh.current) {
        latestAppliedRefresh.current = requestSequence
        setSummary(response.data)
      }
    } catch {
      // Announcement loading must not prevent normal merchant work.
    } finally {
      pendingRefreshes.current -= 1
      if (pendingRefreshes.current === 0) setSummaryPending(false)
    }
  }, [])

  const urgentAnnouncementRequested = Boolean(summary.urgentAnnouncement)
  const urgentAnnouncementActive =
    summaryPending || urgentModalVisible || urgentAnnouncementRequested
  const urgentModalOpen = urgentAnnouncementRequested && agreementDialogsClosed
  urgentAnnouncementActiveRef.current = urgentAnnouncementActive

  useEffect(() => {
    if (urgentModalOpen) setUrgentModalVisible(true)
  }, [urgentModalOpen])

  const handleAgreementDialogsClosed = useCallback(() => {
    setAgreementDialogsClosed(true)
  }, [])

  useEffect(() => {
    refresh()
    return subscribeAnnouncementChanges(refresh)
  }, [refresh])

  const loadRecent = async () => {
    try {
      const response = await listInboxAnnouncements({
        page: 1,
        size: 5,
        unreadOnly: true,
      })
      setRecent(response.data.items)
    } catch {
      setRecent([])
    }
  }

  const confirmUrgent = async () => {
    if (!summary.urgentAnnouncement) return
    setConfirming(true)
    try {
      await confirmAnnouncement(summary.urgentAnnouncement.id)
      await refresh()
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '確認失敗，請重試')
    } finally {
      setConfirming(false)
    }
  }

  const dismissBanner = () => {
    sessionStorage.setItem(DISMISS_KEY, '1')
    setBannerDismissed(true)
  }

  const announcementHeader = (
    <div className="announcement-header-slot">
      {summary.importantAnnouncement && !bannerDismissed && (
        <div
          role="status"
          className="announcement-important-inline flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900"
        >
          <span
            className="announcement-important-title"
            title={summary.importantAnnouncement.title}
          >
            {summary.importantAnnouncement.title}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 shrink-0 px-1 text-xs"
            onClick={() =>
              navigate(
                `/dashboard/announcements?id=${summary.importantAnnouncement?.id}`,
              )
            }
          >
            查看詳情
          </Button>
          <button
            type="button"
            aria-label="關閉重要公告"
            onClick={dismissBanner}
            className="shrink-0 rounded p-1 focus-visible:outline-2 focus-visible:outline-primary"
          >
            <X size={12} />
          </button>
        </div>
      )}
      <div className="announcement-bell-slot">
        <Popover.Root
          open={popoverOpen}
          onOpenChange={(open) => {
            setPopoverOpen(open)
            if (open) void loadRecent()
          }}
        >
          <Popover.Trigger
            render={
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                aria-label={`系統公告，${summary.unreadCount} 則未讀`}
              />
            }
          >
            <Bell size={17} />
            {summary.unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 rounded-full bg-red-600 px-1.5 text-[10px] text-white">
                {summary.unreadCount > 99 ? '99+' : summary.unreadCount}
              </span>
            )}
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Positioner align="end" sideOffset={8} className="z-50">
              <Popover.Popup className="announcement-popover rounded-xl border border-border bg-white p-2 text-foreground shadow-lg">
                <Popover.Title className="sr-only">未讀公告</Popover.Title>
                {recent.length ? (
                  <ul className="divide-y divide-border">
                    {recent.map((item) => (
                      <li key={item.id}>
                        <Button
                          variant="ghost"
                          className="announcement-row-button h-auto min-h-14 w-full justify-start whitespace-normal px-4 py-3 text-left"
                          onClick={() => {
                            setPopoverOpen(false)
                            navigate(`/dashboard/announcements?id=${item.id}`)
                          }}
                        >
                          {item.title}
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="p-4 text-center text-sm text-muted-foreground">
                    沒有未讀公告
                  </p>
                )}
                <Button
                  variant="ghost"
                  className="w-full text-primary"
                  onClick={() => {
                    setPopoverOpen(false)
                    navigate('/dashboard/announcements')
                  }}
                >
                  查看全部公告
                </Button>
              </Popover.Popup>
            </Popover.Positioner>
          </Popover.Portal>
        </Popover.Root>
      </div>
    </div>
  )

  return (
    <>
      {children(
        announcementHeader,
        urgentAnnouncementActive,
        handleAgreementDialogsClosed,
      )}
      <Modal
        open={urgentModalOpen}
        onOpenChange={() => {}}
        afterClose={() => setUrgentModalVisible(false)}
        title="緊急系統公告"
        closable={false}
        footer={
          <Button disabled={confirming} onClick={confirmUrgent}>
            {confirming ? '確認中...' : '我已閱讀並知悉'}
          </Button>
        }
      >
        <h2 className="mb-3 font-semibold">
          {summary.urgentAnnouncement?.title}
        </h2>
        <p className="announcement-body text-sm">
          {summary.urgentAnnouncement?.content}
        </p>
      </Modal>
    </>
  )
}
